import { appointmentColors, appointmentTooltipSx } from '../shared/appointmentColors';
import AppointmentMarker from '../shared/AppointmentMarker';
import { formatInTimeZone } from 'date-fns-tz';
import { eventOnCalendarDay } from '../../../../../../platform/util/calendarTimezone';
import { formatClockText } from '../../../../../../platform/util/clockSystem';
import React, { useRef } from "react";
import { Typography, Chip, Tooltip, Box } from "@mui/material";
import moment from "moment";
import { addMinutes, format } from "date-fns";
import BookItOrCreate from "../bookItOrCreate";
import { useCalendar } from "../calendarContext";

const BookingTypeList = ({ date, showEventDetails, showEventMarker, day, month }) => {
  const { selectedDate, bookingDatas, myBookingTypes, displayClock, displayTimezone } = useCalendar();
  const bookItOrCreateRef = useRef(null);

  const getDisplayTitle = (booking) => {
    const source = String(booking?.source || "").trim().toLowerCase();
    const isGoogle = source.includes("google");
    const rawTitle =
      booking?.bookingTypeName ||
      booking?.title ||
      booking?.summary ||
      booking?.inviteeName ||
      "Event";
    const normalized = String(rawTitle || "").trim();
    if (!isGoogle) return normalized || "Event";
    const cleaned = normalized
      .replace(/\bgoogle\b/gi, "")
      .replace(/\bcalendar\b/gi, "")
      .replace(/\bevent\b/gi, "")
      .replace(/\bmeeting\b/gi, "")
      .replace(/[:\-|]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return cleaned || "Event";
  };

  const getDurationLabel = (booking) => {
    const durationMinutes = Number(booking?.length || booking?.bookingType?.length || 0);
    if (!durationMinutes || Number.isNaN(durationMinutes)) return "Duration unavailable";
    if (durationMinutes >= 60) {
      const hours = durationMinutes / 60;
      return Number.isInteger(hours)
        ? `${hours} hr${hours === 1 ? "" : "s"}`
        : `${hours.toFixed(1)} hrs`;
    }
    return `${durationMinutes} min`;
  };

  const getTooltipContent = (booking, title) => {
    const start = new Date(booking.time);
    const durationMinutes = Number(booking?.length || booking?.bookingType?.length || 0);
    const end = durationMinutes ? addMinutes(start, durationMinutes) : null;
    const timeRange = booking.isAllDay
      ? "All day"
      : end
      ? `${formatInTimeZone(start, displayTimezone, "h:mm a").toLowerCase()} - ${formatInTimeZone(end, displayTimezone, "h:mm a").toLowerCase()}`
      : formatInTimeZone(start, displayTimezone, "h:mm a").toLowerCase();

    return (
      <Box sx={{ py: 0.25 }}>
        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, lineHeight: 1.35 }}>
          {title}
        </Typography>
        <Typography sx={{ mt: 0.35, fontSize: "0.74rem", lineHeight: 1.45 }}>
          {formatClockText(timeRange, displayClock)}
        </Typography>
        <Typography sx={{ fontSize: "0.72rem", opacity: 0.9 }}>
          {getDurationLabel(booking)}
        </Typography>
      </Box>
    );
  };

  // Return null if there are no bookings.
  if (!bookingDatas || bookingDatas.length === 0) return null;

  // Filter bookings to only include those matching the current day.
  const dayBookings = bookingDatas.filter((booking) => {
    return eventOnCalendarDay(booking, date || selectedDate, displayTimezone);
  });

  // Group bookings by a key: either "all-day" or the formatted time + booking type name.
  const groupedDayBookings = dayBookings.reduce((acc, booking) => {
    const bookingDate = new Date(booking.time);
    const timeKey = booking.isAllDay ? "all-day" : formatInTimeZone(bookingDate, displayTimezone, "h:mm a").toLowerCase();
    const bookingTitle = getDisplayTitle(booking);
    const groupKey = `${timeKey}-${bookingTitle}`;
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(booking);
    return acc;
  }, {});

  const groupsArray = Object.values(groupedDayBookings);
  // Sort groups: "all-day" groups first, then by time.
  groupsArray.sort((a, b) => {
    if (a[0].isAllDay) return -1;
    if (b[0].isAllDay) return 1;
    return new Date(a[0].time) - new Date(b[0].time);
  });

  const maxVisibleGroups = showEventDetails ? 2 : 0;
  const visibleGroups = groupsArray.slice(0, maxVisibleGroups);
  const hiddenCount = Math.max(groupsArray.length - visibleGroups.length, 0);

  return (
    <Box sx={{ width: "100%", position: "relative", pt: showEventDetails ? 0.35 : 0, px: 0.25 }}>
      {/* Render event markers if there are any bookings */}
      {dayBookings.length > 0 && (
        <Box sx={{ width: "100%" }}>
          {/* Single marker for the day */}
          {!showEventDetails && showEventMarker && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                mt: 0,
                pointerEvents: "none",
              }}
            >
              <AppointmentMarker />
            </Box>
          )}

          {showEventDetails && (
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 0.45 }}>
              {visibleGroups.map((group, index) => {
              const booking = group[0];
              const bookingDate = new Date(booking.time);
              const bookingTypeName = getDisplayTitle(booking);
              // Use "all-day" if applicable, otherwise format the time.
              const timeLabel = booking.isAllDay
                ? "All day: "
                : `${formatInTimeZone(bookingDate, displayTimezone, "h:mm a").toLowerCase()} - `;
              // Get booking type color from myBookingTypes
              const bookingTypeObj = myBookingTypes?.find(
                (val) => parseInt(val.id) === parseInt(booking.bookingTypeId)
              );
              const color = bookingTypeObj?.color || (
                String(booking?.source || "").toLowerCase().includes("google") ? "#34a853" : "gray"
              );

              return (
                <Tooltip
                  key={index}
                  componentsProps={{ tooltip: { sx: appointmentTooltipSx }, arrow: { sx: { color: theme => appointmentColors(theme).background } } }}
                  title={getTooltipContent(booking, bookingTypeName)}
                  arrow
                  placement="top"
                  enterDelay={120}
                >
                  <Box
                    onClick={() => bookItOrCreateRef.current("update", group, day, month)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.55,
                      cursor: "pointer",
                      textAlign: "left",
                      width: "100%",
                      minWidth: 0,
                      px: 0.55,
                      py: 0.35,
                      borderRadius: 1.75,
                      backgroundColor: "background.paper",
                      border: 1, borderColor: "divider",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: 7,
                        width: 7,
                        borderRadius: "999px",
                        background: color,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "0.69rem",
                        lineHeight: 1.25,
                        color: "text.primary",
                        fontWeight: 600,
                        minWidth: 0,
                        flex: 1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {formatClockText(timeLabel, displayClock)}
                      {bookingTypeName}
                    </Typography>
                    {group.length > 1 && (
                      <Tooltip title={`${group.length} events in this time slot`} arrow>
                        <Chip
                          label={group.length}
                          size="small"
                          sx={{
                            height: 18,
                            borderRadius: "999px",
                            bgcolor: "rgba(219,234,254,0.85)",
                            color: "#1d4ed8",
                            fontSize: "0.62rem",
                            fontWeight: 700,
                            flexShrink: 0,
                            "& .MuiChip-label": { px: 0.55 },
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Tooltip>
              );
              })}
              {hiddenCount > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    px: 0.2,
                  }}
                >
                  <Chip
                    label={`+${hiddenCount} more`}
                    size="small"
                    sx={{
                      height: 18,
                      borderRadius: "999px",
                      backgroundColor: "rgba(21,49,76,0.08)",
                      border: "1px solid rgba(21,49,76,0.12)",
                      color: "text.secondary",
                      fontSize: "0.62rem",
                      fontWeight: 700,
                      "& .MuiChip-label": { px: 0.7 },
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
      <BookItOrCreate triggerDialog={(handler) => (bookItOrCreateRef.current = handler)} />
    </Box>
  );
};

export default BookingTypeList;
